/**
 * Ball generator — baseball, basketball, American football, and soccer ball
 * with geometrically accurate, recessed seam grooves carved into the
 * surface (not overlay lines or textures).
 *
 * The user chooses a ball `type` from a fixed enum; sizing is locked to
 * the official pro-league regulation spec for each type (any `scale`
 * adjustment has to go through the transform panel like every other
 * SceneObject). Two construction strategies live underneath:
 *
 *  - Panel-based (basketball, soccer): the ball is described as a set of
 *    spherical polygons, each tessellated with a V-groove along its
 *    boundary. Adjacent panels meet at the groove bottom.
 *
 *  - Curve-based (baseball, football): a base UV-sphere or revolved-
 *    spheroid mesh is carved by pushing vertices near a parametric seam
 *    curve inward by a smoothstep ramp.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre. All regulation
 * dimensions below come from the governing body's rulebook (MLB, NBA
 * size 7, NFL, FIFA size 5).
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import {
  baseballSeamCurve,
  basketballPanels,
  buildPanelPatch,
  buildRevolvedMeshRaw,
  buildSphereMeshRaw,
  carveCurveGrooves,
  finaliseRawMesh,
  footballSeamCurve,
  type SphericalPanel,
  soccerBallPanels,
} from "./ballGeom.ts";
import { concatPrimitiveGeometries } from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import { defineGenerator, enumParam, paramsFromSpecs } from "./types.ts";

// ---------------------------------------------------------------------------
// Regulation dimensions — sourced from the governing-body rulebooks
// ---------------------------------------------------------------------------

/**
 * Baseball — MLB Rule 3.01: circumference 9 to 9¼ inches (228.6–234.95 mm).
 * Taking the midpoint ⇒ diameter ≈ 73.7 mm ⇒ radius 0.0368 m.
 */
const BASEBALL_RADIUS = 0.0368;

/**
 * Basketball — NBA/FIBA Size 7 (men's): circumference 29.5 in (749.3 mm)
 * ⇒ diameter 0.2385 m ⇒ radius 0.1193 m.
 */
const BASKETBALL_RADIUS = 0.11925;

/**
 * Soccer ball — FIFA Size 5: circumference 68–70 cm (midpoint 69 cm)
 * ⇒ diameter 0.2197 m ⇒ radius 0.1099 m.
 */
const SOCCER_RADIUS = 0.10985;

/**
 * American football — NFL Rule 2: long axis ≈ 11 in (0.2794 m tip-to-tip),
 * short-axis circumference ≈ 21⅛ in (0.5366 m) ⇒ equatorial radius ~0.0854 m.
 *
 * Silhouette is a **lemon of revolution**, not a prolate spheroid: the
 * long-axis cross-section is a circular arc whose chord runs along the
 * long axis from −half-length to +half-length and whose midpoint bulges
 * out to the equatorial radius. This is the shape Wikipedia describes
 * as a "lemon" or "spindle", and it's what a real football looks like —
 * the tips come in at a finite, non-zero angle, producing a genuinely
 * pointed silhouette.
 *
 * A pure prolate spheroid (the mathematically strict reading of
 * "ellipsoid of revolution") was tried first and read as too rounded at
 * the tips. That surface has a vertical tangent at the pole — its
 * silhouette is smooth rather than pointed, which is wrong for a
 * football even though it matches the textbook definition.
 */
const FOOTBALL_LENGTH = 0.2794;
const FOOTBALL_EQUATOR_R = 0.0854;

/**
 * Football profile — 33 (z, r) samples along a lemon arc, cosine-spaced
 * along the long axis so the high-curvature tip region is captured at
 * sub-millimetre resolution.
 *
 * Arc geometry: for half-length `a` and equatorial radius `c`, the arc's
 * central angle satisfies tan(θ/2) = c / a, so θ = 2·atan(c / a). From
 * θ we get the arc radius R = a / sin θ and its centre offset from the
 * long axis d = R · cos θ. The profile is then r(z) = √(R² − z²) − d,
 * which equals c at z = 0 and goes to 0 at z = ±a with slope ∓a/(R·cos θ)
 * — a finite, non-vertical tangent, i.e. a pointed tip.
 */
function buildFootballProfile(): [number, number][] {
  const half = FOOTBALL_LENGTH / 2;
  const c = FOOTBALL_EQUATOR_R;

  const theta = 2 * Math.atan(c / half);
  const rArc = half / Math.sin(theta);
  const offset = rArc * Math.cos(theta);

  const steps = 32;
  const profile: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    // Cosine-spaced parameter: θ ∈ [0, π] → t = cos θ ∈ [1, −1].
    const thetaSample = (i / steps) * Math.PI;
    const t = Math.cos(thetaSample);
    const z = -t * half;
    const r = Math.max(0, Math.sqrt(Math.max(0, rArc * rArc - z * z)) - offset);
    profile.push([z, r]);
  }
  return profile;
}

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface BallParams extends GeneratorParams {
  type: string;
}

const BALL_TYPE_OPTIONS = [
  { value: "basketball", label: "Basketball" },
  { value: "soccer", label: "Soccer Ball" },
  { value: "football", label: "American Football" },
  { value: "baseball", label: "Baseball" },
] as const;

const BALL_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "type",
    label: "Type",
    type: "enum",
    default: "basketball",
    options: BALL_TYPE_OPTIONS,
  },
];

// ---------------------------------------------------------------------------
// Per-type builders
// ---------------------------------------------------------------------------

/** Concatenate a list of spherical panel meshes into one ball geometry. */
function buildPanelBall(
  panels: readonly SphericalPanel[],
  radius: number,
  grooveDepth: number,
  grooveWidth: number,
  subdivisions: number,
): PrimitiveGeometry {
  const parts: PrimitiveGeometry[] = panels.map((p) =>
    buildPanelPatch(p, radius, grooveDepth, grooveWidth, subdivisions),
  );
  return concatPrimitiveGeometries(parts);
}

function buildBasketball(): PrimitiveGeometry {
  // Groove ≈ 3 mm wide, 1.5 mm deep — visible at arm's length, still
  // subtle relative to a Ø 0.24 m ball. `grooveWidth` is the angular
  // half-width of the bevel on the unit sphere.
  const grooveDepth = 0.0015;
  const grooveHalfArc = 0.0015 / BASKETBALL_RADIUS;
  // Each spherical triangle gets fan-triangulated into 3 sub-triangles;
  // N = 14 keeps the dome smooth and the groove clean.
  return buildPanelBall(
    basketballPanels(),
    BASKETBALL_RADIUS,
    grooveDepth,
    grooveHalfArc,
    14,
  );
}

function buildSoccerBall(): PrimitiveGeometry {
  // Panels are smaller than a basketball's, so the groove is shallower
  // (1.0 mm) and narrower to stay proportional.
  const grooveDepth = 0.0012;
  const grooveHalfArc = 0.0013 / SOCCER_RADIUS;
  // Hexagons and pentagons only need modest subdivision — 6 keeps the
  // vertex count reasonable across 32 panels while still reading as round.
  return buildPanelBall(
    soccerBallPanels(),
    SOCCER_RADIUS,
    grooveDepth,
    grooveHalfArc,
    6,
  );
}

function buildFootball(): PrimitiveGeometry {
  const profile = buildFootballProfile();
  // 128 azimuthal segments gives enough vertex density at the equator
  // (~4 mm spacing) for the narrow longitudinal groove to read cleanly
  // without visible stair-stepping.
  const { positions, faces } = buildRevolvedMeshRaw(profile, 128);
  // Seam: one great-circle curve in the XZ plane running tip-to-tip.
  const curve = footballSeamCurve(profile, 256);
  carveCurveGrooves(
    positions,
    [0, 0, 0],
    [curve],
    0.0012, // depth (~1.2 mm)
    0.002, // half-width (~2 mm)
  );
  return finaliseRawMesh(positions, faces);
}

function buildBaseball(): PrimitiveGeometry {
  // 160 × 80 UV sphere gives ~1.4 mm vertex spacing at the equator —
  // the groove half-width below is 2.5 mm, so the bevel ramp always
  // covers at least three mesh vertices on each side of the seam and
  // reads as a proper valley rather than a hair-line.
  //
  // Earlier parameters (0.4 mm deep, 0.8 mm half-width, 96 × 48 sphere)
  // produced a groove narrower than the mesh resolution, so most
  // vertices never entered the bevel band and the seam only caught
  // the occasional vertex — the user reported it as "barely visible".
  const { positions, faces } = buildSphereMeshRaw(BASEBALL_RADIUS, 160, 80);
  const curve = baseballSeamCurve(BASEBALL_RADIUS, 768);
  carveCurveGrooves(
    positions,
    [0, 0, 0],
    [curve],
    0.001, // depth (~1 mm, matching the ratio the basketball uses)
    0.0025, // half-width (~2.5 mm bevel ramp on each side)
  );
  return finaliseRawMesh(positions, faces);
}

// ---------------------------------------------------------------------------
// Build entry point
// ---------------------------------------------------------------------------

function buildBall(
  p: BallParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const type = enumParam(p, "type", "basketball");
  let geometry: PrimitiveGeometry;
  switch (type) {
    case "basketball":
      geometry = buildBasketball();
      break;
    case "soccer":
      geometry = buildSoccerBall();
      break;
    case "football":
      geometry = buildFootball();
      break;
    case "baseball":
      geometry = buildBaseball();
      break;
    default:
      geometry = buildBasketball();
      break;
  }
  return { kind: "mesh", geometry };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const BALL_GENERATOR: Generator = defineGenerator<BallParams>({
  id: "ball",
  label: "Ball",
  category: "prop",
  paramSpecs: BALL_SPECS,
  defaultParams: paramsFromSpecs(BALL_SPECS) as BallParams,
  build: buildBall,
});
